import { PersistenceLevel } from "../../config/PersistenceLevels";
import Entity, { GameEvents } from "../../core/entity/Entity";
import Level from "../Level";
import { RunEntity } from "../LevelEntity";
import GameOverScreen from "../ui/GameOverScreen";
import OffScreenIndicator from "../ui/OffScreenIndicator";
import PauseMenu from "../ui/PauseMenu";
import ScoreDisplay from "../ui/ScoreDisplay";
import UpgradeMenu from "../ui/UpgradeMenu";
import ScoreManager from "./ScoreManager";
import UpgradeManager from "./UpgradeManager";

// Game flow constants
const INITIAL_LEVEL = 1;

export default class GameFlowManager extends RunEntity implements Entity {
  id = "gameFlowManager";
  pausable = false; // Continue to work even when paused
  persistenceLevel = PersistenceLevel.Game; // Survive level transitions

  private _currentLevel: number = INITIAL_LEVEL;
  private _isGameOver: boolean = false;

  get currentLevel(): number {
    return this._currentLevel;
  }

  onAdd() {
    if (!this.game) return;

    // Set up core game systems as children
    this.addChild(new ScoreManager());
    this.addChild(new ScoreDisplay());
    this.addChild(new UpgradeManager());
    this.addChild(new OffScreenIndicator());

    // Start the first level
    this.createLevel(this._currentLevel);
  }

  onLevelComplete({ level }: GameEvents["levelComplete"]) {
    // Add upgrade menu as child of GameFlowManager
    this.addChild(new UpgradeMenu(level));
  }

  onUpgradeChosen({}: GameEvents["upgradeChosen"]) {
    // The UpgradeManager already handles this via its onUpgradeChosen method
    this.startNextLevel();
  }

  onGameOver({}: GameEvents["gameOver"]) {
    console.log("Game Over! Showing game over screen...");

    // Set game over state
    this._isGameOver = true;

    // Destroy the turret to stop shooting
    const turrets = this.game?.entities.getTagged("turret") || [];
    for (const turret of turrets) {
      turret.destroy();
    }

    // Get final stats
    const scoreManager = this.game?.entities.getById(
      "scoreManager",
    ) as ScoreManager;
    const finalScore = scoreManager?.score || 0;
    const finalLevel = this._currentLevel;

    // Show game over screen (now has its own Main Menu button)
    this.addChild(new GameOverScreen(finalScore, finalLevel));
  }

  onKeyDown({ key }: GameEvents["keyDown"]) {
    if (!this.game) return;

    // Don't process any game controls after game over
    if (this._isGameOver) return;

    switch (key) {
      case "Escape":
        this.game.togglePause();
        break;
      default:
        break;
    }
  }

  onPause() {
    // Show pause menu when game is paused
    this.game?.addEntity(new PauseMenu());
  }

  onUnpause() {
    // Hide pause menu when game is unpaused
    const pauseMenus = this.game?.entities.getTagged("pauseMenu") || [];
    for (const pauseMenu of pauseMenus) {
      pauseMenu.destroy();
    }
  }

  private startNextLevel() {
    this._currentLevel++;
    this.createLevel(this._currentLevel);
  }

  private createLevel(level: number) {
    this.addChild(new Level(level));
  }

  private getCurrentLevel(): Level | undefined {
    return this.game?.entities.getById("currentLevel") as Level;
  }
}
